import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Flame, Sparkles, Sun, Shield, Stethoscope, Dumbbell, Brain, Heart, Clock, Tag, Zap, BookOpen } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

// Import stack images
import metabolicResetBackground from "@/assets/metabolic-reset-stack.jpg";
import springBreakBackground from "@/assets/spring-break-stack.png";
import summerMaintenanceBackground from "@/assets/summer-maintenance-stack.png";
import executivePerformanceBackground from "@/assets/executive-performance-stack.png";
import fluDefenseBackground from "@/assets/flu-defense-stack.png";
import gutRestorationBackground from "@/assets/gut-restoration-stack.png";
import surgeryBackground from "@/assets/surgery-stack.jpg";
import athleticBackground from "@/assets/athletic-stack.jpg";
import stressBurnoutBackground from "@/assets/stress-burnout-stack.png";
import longevityBackground from "@/assets/longevity-stack.jpg";
import radianceBackground from "@/assets/radiance-skin-stack.png";
import harmonyBackground from "@/assets/harmony-stack.png";

const backgroundImages: Record<string, string> = {
  metabolic: metabolicResetBackground,
  springbreak: springBreakBackground,
  summer: summerMaintenanceBackground,
  executive: executivePerformanceBackground,
  radiance: radianceBackground,
  flu: fluDefenseBackground,
  gut: gutRestorationBackground,
  surgery: surgeryBackground,
  athletic: athleticBackground,
  stress: stressBurnoutBackground,
  longevity: longevityBackground,
  harmony: harmonyBackground,
};

// Map treatment IDs to blog post slugs
const blogSlugMap: Record<string, string> = {
  metabolic: "metabolic-reset-stack-transformation",
  springbreak: "spring-break-stack-transformation",
  summer: "summer-stack-maintenance",
  executive: "executive-performance-stack-guide",
  radiance: "radiance-skin-rejuvenation-stack",
  flu: "flu-defense-stack-immune",
  gut: "gut-restoration-stack-healing",
  surgery: "surgery-stack-recovery",
  athletic: "workout-athletic-stack-performance",
  stress: "stress-burnout-stack-recovery",
  longevity: "longevity-stack-healthspan",
  harmony: "harmony-perimenopause-stack",
};

interface TreatmentCategory {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  description: string;
  peptides: string[];
  benefits: string[];
  originalPrice: number;
  salePrice: number;
  savings: number;
  duration: string;
  promotion: string;
  optionalAddons?: string[];
  programNote?: string;
}

const treatments: TreatmentCategory[] = [
  {
    id: "metabolic",
    title: "Metabolic Reset + Repair",
    subtitle: "Weight Loss & Metabolic Optimization",
    icon: Flame,
    description: "A comprehensive protocol for weight-loss resistance, metabolic syndrome, and insulin resistance. Combining GLP-1 therapy with mitochondrial support and growth hormone optimization for sustainable metabolic transformation.",
    peptides: ["GLP-1 Harmony", "MOTS-c", "CJC/Ipamorelin", "Wolverine"],
    benefits: ["Sustainable weight management", "Enhanced metabolic function", "Cellular repair & recovery", "Growth hormone optimization"],
    originalPrice: 1299,
    salePrice: 1039,
    savings: 260,
    duration: "3-4 months",
    promotion: "20% off first order",
  },
  {
    id: "springbreak",
    title: "Spring Break Stack",
    subtitle: "Rapid Body Transformation",
    icon: Sparkles,
    description: "Short-term transformation protocol for events, travel, and weddings. Combines weight management, skin rejuvenation, and growth hormone support for a complete aesthetic overhaul.",
    peptides: ["GLP-1 Harmony", "MOTS-c", "GLOW", "GH Blend"],
    benefits: ["Rapid body composition changes", "Enhanced skin radiance", "Metabolic acceleration", "Comprehensive transformation"],
    originalPrice: 1395,
    salePrice: 1116,
    savings: 279,
    duration: "3 months optimal",
    promotion: "20% off first order",
  },
  {
    id: "summer",
    title: "Summer Maintenance",
    subtitle: "Modular Wellness Maintenance",
    icon: Sun,
    description: "Maintenance mode protocol for preventing metabolic rebound. Flexible options to maintain your transformation with ongoing provider guidance.",
    peptides: ["GLP-1 (choice)", "Regenerative (choice)", "GH (choice)", "MOTS-c"],
    benefits: ["Sustainable long-term results", "Flexible protocol options", "Cost-effective maintenance", "Continued metabolic support"],
    originalPrice: 1395,
    salePrice: 1116,
    savings: 279,
    duration: "6 months",
    promotion: "20% off first order",
  },
  {
    id: "executive",
    title: "Executive Performance Stack",
    subtitle: "Peak Cognitive & Physical Optimization",
    icon: Zap,
    description: "The ultimate optimization protocol for leaders who demand maximum performance. Three tiers of precision peptide protocols targeting cognitive clarity, stress resilience, cellular energy, and longevity—for executives who refuse to compromise.",
    peptides: ["Semax", "Selank", "MOTS-c", "Thymosin Alpha-1", "BPC-157", "Pinealon", "PE-22-28", "Epithalon", "NAD+"],
    benefits: ["Elite cognitive performance", "Stress resilience & recovery", "Cellular energy optimization", "Sustained peak performance"],
    originalPrice: 1869,
    salePrice: 1495,
    savings: 374,
    duration: "Ongoing optimization",
    promotion: "20% off first order",
  },
  {
    id: "radiance",
    title: "RADIANCE Stack",
    subtitle: "Skin Rejuvenation & Anti-Aging",
    icon: Sparkles,
    description: "Transform your skin from the cellular level with the most advanced peptide protocol designed for aesthetic optimization. This isn't skincare—it's cellular rejuvenation. Stimulating collagen production, repairing cellular damage, and restoring youthful tissue quality from within.",
    peptides: ["GHK-Cu", "BPC-157", "TB-500", "Epithalon"],
    benefits: ["Enhanced collagen production", "Skin barrier restoration", "Tissue regeneration", "Cellular anti-aging"],
    originalPrice: 1494,
    salePrice: 1195,
    savings: 299,
    duration: "3-6 months optimal",
    promotion: "20% off first order",
    optionalAddons: ["Melanotan II"],
  },
  {
    id: "harmony",
    title: "HARMONY Stack",
    subtitle: "Perimenopause & Menopause Restoration",
    icon: Heart,
    description: "The first comprehensive peptide protocol designed specifically for women navigating hormonal transitions. Restore hormonal rhythm, sleep quality, skin vitality, and emotional balance—reclaim your body and your life.",
    peptides: ["Kisspeptin-10", "Oxytocin", "GLOW Blend", "Epithalon", "KPV"],
    benefits: ["Hormonal rhythm restoration", "Sleep & mood optimization", "Skin & tissue rejuvenation", "Inflammation modulation"],
    originalPrice: 1395,
    salePrice: 1116,
    savings: 279,
    duration: "3-6 months",
    promotion: "20% off first order",
    optionalAddons: ["PT-141 (Bremelanotide)"],
  },
  {
    id: "flu",
    title: "Flu Defense Stack",
    subtitle: "Immune System Fortification",
    icon: Shield,
    description: "Proactive immune support for acute illness, frequent infections, and seasonal prep. Builds resilience and accelerates recovery when you need it most.",
    peptides: ["Thymosin Alpha-1", "LL-37", "KPV"],
    benefits: ["Enhanced immune resilience", "Faster illness recovery", "Inflammation modulation", "Seasonal protection"],
    originalPrice: 599,
    salePrice: 479,
    savings: 120,
    duration: "2-4 weeks acute",
    promotion: "20% off first order",
  },
  {
    id: "gut",
    title: "Gut Restoration",
    subtitle: "Digestive & Intestinal Healing",
    icon: Heart,
    description: "Targeted protocol for IBS, IBD, leaky gut, SIBO, and chronic inflammation. Science-based healing approach for gut barrier repair and microbiome support.",
    peptides: ["BPC-157", "KPV", "LL-37"],
    benefits: ["Gut barrier restoration", "Reduced inflammation", "Microbiome support", "Digestive comfort"],
    originalPrice: 549,
    salePrice: 439,
    savings: 110,
    duration: "4-6 weeks minimum",
    promotion: "20% off first order",
  },
  {
    id: "surgery",
    title: "Pre/Post Surgery",
    subtitle: "Surgical Recovery Optimization",
    icon: Stethoscope,
    description: "Comprehensive protocol for elective procedures, orthopedic, and cosmetic surgery. Prepares your body for optimal healing and accelerates post-operative recovery.",
    peptides: ["GHK-Cu", "Wolverine", "Thymosin Alpha-1"],
    benefits: ["Enhanced tissue repair", "Accelerated wound healing", "Reduced recovery time", "Immune system support"],
    originalPrice: 665,
    salePrice: 532,
    savings: 133,
    duration: "2-4 weeks pre + 6-10 weeks post",
    promotion: "20% off first order",
  },
  {
    id: "athletic",
    title: "Workout / Athletic Stack",
    subtitle: "Performance & Recovery",
    icon: Dumbbell,
    description: "Elite performance protocol for training performance, recovery, and body recomposition. Optimizes recovery, enhances endurance, and supports lean muscle development.",
    peptides: ["Micro-dose GLP-1 Revolution", "MOTS-c", "Wolverine", "GH Blend"],
    benefits: ["Enhanced performance", "Faster workout recovery", "Lean muscle support", "Endurance optimization"],
    originalPrice: 1095,
    salePrice: 876,
    savings: 219,
    duration: "3-4 months",
    promotion: "20% off first order",
  },
  {
    id: "stress",
    title: "Stress & Burnout Stack",
    subtitle: "Adrenal & Cognitive Recovery",
    icon: Brain,
    description: "Recovery protocol for chronic stress, poor sleep, and cognitive overload. Supports adrenal function, cognitive clarity, and cellular rejuvenation for sustained resilience.",
    peptides: ["MOTS-c", "Thymosin Alpha-1", "KPV", "Epithalon"],
    benefits: ["Stress resilience support", "Cognitive clarity", "Adrenal recovery", "Cellular rejuvenation"],
    originalPrice: 829,
    salePrice: 663,
    savings: 166,
    duration: "2-3 months with Epithalon cycles",
    promotion: "20% off first order",
  },
  {
    id: "longevity",
    title: "Longevity Stack",
    subtitle: "Anti-Aging & Cellular Health",
    icon: Clock,
    description: "Comprehensive anti-aging protocol for healthy aging, cognitive preservation, and tissue integrity. Targeting cellular health, telomere support, and metabolic optimization.",
    peptides: ["Epithalon", "MOTS-c", "GLOW", "Micro-dose GLP-1 Harmony"],
    benefits: ["Telomere support", "Cellular rejuvenation", "Metabolic health", "Healthy aging"],
    originalPrice: 1359,
    salePrice: 1087,
    savings: 272,
    duration: "Long-term (years)",
    promotion: "20% off first order",
  },
];

interface PeptideTreatmentCardsProps {
  onOpenModal?: () => void;
}

const PeptideTreatmentCards = ({ onOpenModal }: PeptideTreatmentCardsProps) => {
  const navigate = useNavigate();
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  // Track stack product click
  const trackStackClick = useCallback(async (stackId: string, stackTitle: string, action: 'view' | 'book') => {
    try {
      await supabase.from('cta_clicks').insert({
        cta_type: 'peptide_stack',
        cta_location: `${stackId}_${action}`,
        source_url: window.location.pathname,
        session_id: sessionStorage.getItem('session_id') || crypto.randomUUID(),
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      console.error('Failed to track stack click:', error);
    }
  }, []);

  const handleImageLoad = useCallback((id: string) => {
    setLoadedImages((prev) => new Set(prev).add(id));
  }, []);

  const handleCardClick = useCallback((treatment: TreatmentCategory) => {
    const blogSlug = blogSlugMap[treatment.id];
    if (blogSlug) {
      trackStackClick(treatment.id, treatment.title, 'view');
      navigate(`/wellness-blog/${blogSlug}`);
    }
  }, [navigate, trackStackClick]);

  return (
    <>
      {treatments.map((treatment, index) => (
        <section 
          key={treatment.id}
          id={treatment.id} 
          className="py-12 md:py-28 scroll-mt-20 border-b border-white/10 last:border-b-0 bg-gradient-to-br from-[#0D1B2A] via-[#1B263B] to-[#0D1B2A] overflow-hidden"
        >
          <div className="container mx-auto px-0 md:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-0 lg:gap-20 items-center">
                {/* Mobile: Headline & Description first */}
                <div className="space-y-4 lg:hidden px-6">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ffdb4d]/10 border border-[#ffdb4d]/20">
                    <treatment.icon className="w-5 h-5 text-[#ffdb4d]" />
                    <span className="text-sm font-bold text-[#ffdb4d]">{treatment.subtitle}</span>
                  </div>

                  <div className="space-y-3">
                    <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
                      {treatment.title}
                    </h2>
                    <p className="text-base text-white/70 leading-relaxed">
                      {treatment.description}
                    </p>
                  </div>
                </div>

                {/* Image Card - Optimized with native lazy loading */}
                <div 
                  className="relative group cursor-pointer lg:order-2 w-full lg:w-auto mt-6 lg:mt-0"
                  onClick={() => handleCardClick(treatment)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleCardClick(treatment)}
                  aria-label={`View ${treatment.title} details`}
                >
                  {backgroundImages[treatment.id] ? (
                    <div className="aspect-[4/5] md:aspect-square rounded-none lg:rounded-3xl overflow-hidden relative shadow-none lg:shadow-xl hover:shadow-2xl transition-shadow duration-500 bg-[#1B263B]">
                      {/* Optimized Image - Native lazy loading */}
                      <img 
                        src={backgroundImages[treatment.id]}
                        alt={`${treatment.title} peptide stack`}
                        loading={index === 0 ? "eager" : "lazy"}
                        decoding="async"
                        fetchPriority={index === 0 ? "high" : "auto"}
                        onLoad={() => handleImageLoad(treatment.id)}
                        className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 lg:group-hover:scale-110 ${
                          loadedImages.has(treatment.id) ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      {/* Skeleton loader */}
                      {!loadedImages.has(treatment.id) && (
                        <div className="absolute inset-0 bg-gradient-to-br from-[#1B263B] to-[#0D1B2A] animate-pulse" />
                      )}
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 transition-opacity duration-500 group-hover:from-black/70" />
                      {/* Glow effect on hover */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[#ffdb4d]/5" />
                      {/* Content */}
                      <div className="absolute inset-0 flex flex-col justify-end p-8">
                        <div className="text-center space-y-3">
                          {/* Icon */}
                          <treatment.icon className="w-10 h-10 mx-auto text-white/80 group-hover:text-[#ffdb4d] transition-colors duration-500 drop-shadow-lg" />
                          {/* Title */}
                          <div className="text-2xl md:text-3xl font-black text-white group-hover:text-[#ffdb4d] transition-colors duration-500 drop-shadow-lg">
                            {treatment.title}
                          </div>
                          {/* Animated underline */}
                          <div className="w-0 group-hover:w-24 h-0.5 bg-[#ffdb4d] mx-auto transition-all duration-500 ease-out" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Card className="aspect-square rounded-3xl bg-gradient-to-br from-wellness/20 via-wellness/10 to-primary/10 flex items-center justify-center p-8 border-wellness/20 transition-all duration-500 group-hover:shadow-xl group-hover:border-[#ffdb4d]/30">
                      <div className="text-center space-y-4">
                        <treatment.icon className="w-20 h-20 mx-auto text-wellness/40 group-hover:text-[#ffdb4d] transition-colors duration-500" />
                        <div className="text-2xl md:text-3xl font-black text-wellness/40 group-hover:text-[#ffdb4d] transition-colors duration-500">{treatment.title}</div>
                      </div>
                    </Card>
                  )}
                  {/* Decorative elements - hidden on mobile */}
                  <div className="hidden lg:block absolute -top-4 -right-4 w-24 h-24 bg-wellness/10 group-hover:bg-[#ffdb4d]/20 rounded-full blur-2xl transition-colors duration-500" />
                  <div className="hidden lg:block absolute -bottom-8 -left-8 w-32 h-32 bg-wellness/5 group-hover:bg-[#ffdb4d]/10 rounded-full blur-3xl transition-colors duration-500" />
                </div>

                {/* Content Section */}
                <div className="space-y-6 lg:space-y-8 lg:order-1 px-6 lg:px-0 pt-8 lg:pt-0">
                  {/* Desktop only: Badge, Headline, Description */}
                  <div className="hidden lg:block space-y-6">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ffdb4d]/10 border border-[#ffdb4d]/20">
                      <treatment.icon className="w-5 h-5 text-[#ffdb4d]" />
                      <span className="text-sm font-bold text-[#ffdb4d]">{treatment.subtitle}</span>
                    </div>

                    <div className="space-y-6">
                      <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight">
                        {treatment.title}
                      </h2>
                      <p className="text-lg md:text-xl text-white/70 leading-relaxed">
                        {treatment.description}
                      </p>
                    </div>
                  </div>

                  {/* Key Peptides */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold text-white/60 uppercase tracking-wide">Included Peptides</h4>
                    <div className="flex flex-wrap gap-2">
                      {treatment.peptides.map((peptide) => (
                        <span 
                          key={peptide}
                          className="px-4 py-2 bg-[#ffdb4d]/10 text-[#ffdb4d] rounded-full text-sm font-semibold transition-all duration-300 hover:scale-105 hover:bg-[#ffdb4d]/20 hover:shadow-md"
                        >
                          {peptide}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Benefits */}
                  <ul className="space-y-3">
                    {treatment.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#ffdb4d] flex-shrink-0" />
                        <span className="text-white font-medium">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Optional Add-ons */}
                  {treatment.optionalAddons && treatment.optionalAddons.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-bold text-white/60 uppercase tracking-wide">Optional Add-ons</h4>
                      <ul className="space-y-1">
                        {treatment.optionalAddons.map((addon, idx) => (
                          <li key={idx} className="text-sm text-white/70">+ {addon}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Program Note */}
                  {treatment.programNote && (
                    <div className="p-4 bg-[#ffdb4d]/5 border border-[#ffdb4d]/20 rounded-xl">
                      <p className="text-sm text-[#ffdb4d] font-medium">{treatment.programNote}</p>
                    </div>
                  )}

                  {/* CTA */}
                  <div className="space-y-4">
                    <Button 
                      size="lg" 
                      className="text-lg px-8 py-6 h-auto font-bold group bg-[#ffdb4d] hover:bg-[#ffdb4d]/90 text-[#0D1B2A]"
                      onClick={(e) => {
                        e.stopPropagation();
                        trackStackClick(treatment.id, treatment.title, 'book');
                        onOpenModal?.();
                      }}
                    >
                      <Zap className="mr-2 h-5 w-5" />
                      Book Now
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                  
                  {/* Compliance Disclaimer */}
                  <p className="text-xs text-white/50 mt-4 leading-relaxed">
                    *Results vary by individual. These statements have not been evaluated by the FDA. 
                    Peptide protocols are compounded by licensed pharmacies and prescribed by licensed providers 
                    based on individual assessment. Not intended to diagnose, treat, cure, or prevent any disease.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </>
  );
};

export default PeptideTreatmentCards;
